// src/App.jsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { supabase } from './lib/supabaseClient';
import ReactGA from 'react-ga4';

import './styles/AppStyles.css';
import './routes/AuthPage.css'; // Considera renombrar este CSS si es más general o crear uno específico para auth
import { Spinner } from 'react-bootstrap';

// Importar páginas
import LoginPage from './routes/LoginPage';
import RegisterPage from './routes/RegisterPage';
import MainPage from './routes/MainPage';
import PricingPage from './routes/PricingPage';
import NotFoundPage from './routes/NotFoundPage';
import LandingPage from './routes/LandingPage';
import ResetPasswordPage from './routes/ResetPassword';
import AboutPage from './routes/AboutPage';
import ContactPage from './routes/ContactPage';
import ContactSalesPage from './routes/ContactSalesPage';

// MODIFICACIÓN 1: Importar la nueva página de Términos y Condiciones
import TermsAndConditionsPage from './routes/TermsAndConditionsPage'; // Ajusta la ruta si es diferente
// ASUME que tendrás estas páginas también, impórtalas si las creas
import PrivacyPolicyPage from './routes/PrivacyPolicyPage';
// import CookiePolicyPage from './pages/CookiePolicyPage';


const GA4_TRACKING_ID = process.env.REACT_APP_GA4_TRACKING_ID;

const GAListener = () => {
  const location = useLocation();
  useEffect(() => {
    if (GA4_TRACKING_ID) {
      ReactGA.send({ hitType: "pageview", page: location.pathname + location.search + location.hash, title: document.title });
    }
  }, [location]);
  return null;
};

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (GA4_TRACKING_ID) {
      ReactGA.initialize(GA4_TRACKING_ID);
      console.log("GA4 Initialized with ID:", GA4_TRACKING_ID);
    } else {
      console.warn("GA4 Tracking ID not found. Analytics will not be sent.");
    }
  }, []);

  useEffect(() => {
    const initSession = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) console.error('Error getting session:', error.message);
      setUser(session?.user || null);
      setLoading(false);
    };
    initSession();
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log(`[Supabase] Auth event: ${event}`);
      setUser(session?.user || null);
    });
    return () => {
      listener?.subscription?.unsubscribe();
    };
  }, []);

  const PrivateRoute = ({ children }) => {
    return user ? children : <Navigate to="/login" replace />;
  };

  if (loading) return (
    <div className="vh-100 d-flex flex-column justify-content-center align-items-center text-center">
      <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
      <p className="mt-3 fs-5 text-muted">Loading application...</p>
    </div>
  );

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <Router>
        {GA4_TRACKING_ID && <GAListener />}
        <Routes>
          {/* Rutas públicas y de autenticación */}
          <Route path="/" element={user ? <Navigate to="/app" replace /> : <LandingPage />} />
          <Route path="/login" element={user ? <Navigate to="/app" replace /> : <LoginPage setUser={setUser} />} />
          <Route path="/register" element={user ? <Navigate to="/app" replace /> : <RegisterPage setUser={setUser} />} />

          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/contact-sales" element={<ContactSalesPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* MODIFICACIÓN 2: Añadir la ruta para Términos y Condiciones */}
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          {/* ASUME que tendrás estas rutas también, créalas si es necesario */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          {/* <Route path="/cookie-policy" element={<CookiePolicyPage />} /> */}


          {/* Rutas privadas */}
          <Route
            path="/app"
            element={
              <PrivateRoute>
                <MainPage user={user} setUser={setUser} />
              </PrivateRoute>
            }
          />

          {/* Ruta para páginas no encontradas */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
    </GoogleOAuthProvider>
  );
};

export default App;